import {useIssueDeletionState} from '@github-ui/issue-viewer/useIssueDeletionState'
import {createContext, memo, use, useEffect, useMemo, useRef} from 'react'

import {useHandlePaginatedDataRefresh} from '../../data-refresh/use-handle-paginated-data-refresh'

type PaginatedMemexItemsQueryContextType = {
  handlePaginatedDataRefresh: ReturnType<typeof useHandlePaginatedDataRefresh>['handleRefresh']
  issueDeletionTargets: ReturnType<typeof useHandlePaginatedDataRefresh>['issueDeletionTargets']
  getIssueDeletionTargets: ReturnType<typeof useHandlePaginatedDataRefresh>['getIssueDeletionTargets']
  refreshForIssueDeletion: ReturnType<typeof useHandlePaginatedDataRefresh>['refreshForIssueDeletion']
  reportIssueDeletionFailure: ReturnType<typeof useHandlePaginatedDataRefresh>['reportIssueDeletionFailure']
  reportIssueDeletionCompletionFailure: ReturnType<
    typeof useHandlePaginatedDataRefresh
  >['reportIssueDeletionCompletionFailure']
  isRefreshing: boolean
}

const PaginatedMemexItemsQueryContext = createContext<PaginatedMemexItemsQueryContextType | null>(null)

export const PaginatedMemexItemsQueryProvider = memo(function PaginatedMemexItemsQueryProviderComponent({
  children,
}: {
  children: React.ReactNode
}) {
  const {
    handleRefresh: handlePaginatedDataRefresh,
    isRefreshing,
    issueDeletionTargets,
    getIssueDeletionTargets,
    refreshForIssueDeletion,
    reportIssueDeletionFailure,
    reportIssueDeletionCompletionFailure,
  } = useHandlePaginatedDataRefresh()
  const deletions = useIssueDeletionState()
  const reportedDeletionsRef = useRef(new Set<number>())

  useEffect(() => {
    for (const deletion of deletions) {
      if (reportedDeletionsRef.current.has(deletion.id)) continue
      if (deletion.status === 'error') {
        reportedDeletionsRef.current.add(deletion.id)
        reportIssueDeletionFailure()
      } else if (deletion.status === 'success') {
        reportedDeletionsRef.current.add(deletion.id)
        if (deletion.completionError) reportIssueDeletionCompletionFailure(deletion.completionError)
      }
    }
  }, [deletions, reportIssueDeletionCompletionFailure, reportIssueDeletionFailure])

  const value = useMemo(() => {
    // eslint-disable-next-line @github-ui/github-monorepo/no-mixed-stability-context-value -- deletion targets must update with the provider's current loaded query set
    return {
      handlePaginatedDataRefresh,
      isRefreshing,
      issueDeletionTargets,
      getIssueDeletionTargets,
      refreshForIssueDeletion,
      reportIssueDeletionFailure,
      reportIssueDeletionCompletionFailure,
    }
  }, [
    getIssueDeletionTargets,
    handlePaginatedDataRefresh,
    isRefreshing,
    issueDeletionTargets,
    refreshForIssueDeletion,
    reportIssueDeletionFailure,
    reportIssueDeletionCompletionFailure,
  ])

  return <PaginatedMemexItemsQueryContext value={value}>{children}</PaginatedMemexItemsQueryContext>
})

export const usePaginatedMemexItemsQueryContext = () => use(PaginatedMemexItemsQueryContext)
